import { Card, PageHeading } from "@peer-start/ui";
import { CircleCheck, CircleDot, Info, Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAppContext } from "../../../app/use-app-context.js";
import { pageDefinitions } from "../../../app/page-references.js";
import { loadSchoolProfile } from "../data/school-profile.js";

function Status({ complete }: { complete: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${
        complete ? "bg-success text-success-foreground" : "bg-warning text-warning-foreground"
      }`}
    >
      {complete ? (
        <CircleCheck aria-hidden="true" className="size-3.5" />
      ) : (
        <CircleDot aria-hidden="true" className="size-3.5" />
      )}
      {t(complete ? "school.status.complete" : "school.status.inProgress")}
    </span>
  );
}

function EditLink({ step }: { step: number }) {
  const { t } = useTranslation();
  return (
    <Link
      className="inline-flex items-center gap-1 text-sm font-medium text-link hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
      data-control-id={`school.edit.${step}`}
      to={`${pageDefinitions.schoolSetup.path}?step=${step}`}
    >
      <Pencil aria-hidden="true" className="size-3.5" />
      {t("school.edit")}
    </Link>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 text-sm sm:grid-cols-[9rem_1fr]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

export function SchoolPage({ documentsOnly = false }: { documentsOnly?: boolean }) {
  const { t } = useTranslation();
  const { persona } = useAppContext();
  const [profile] = useState(loadSchoolProfile);
  if (persona.kind === "employee") return null;

  const documentsComplete = Boolean(profile.coursePlan && profile.trainingPlan && profile.learningPaths);
  const staff = profile.staffRoles.map((entry) => `${entry.role} (${entry.amount})`).join(", ");

  const documents = (
    <Card>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-medium">{t("school.documents.title")}</h2>
          <Status complete={documentsComplete} />
        </div>
        <EditLink step={3} />
      </div>
      <dl className="space-y-3">
        <Detail
          label={t("school.documents.coursePlan")}
          value={profile.coursePlan ?? t("school.documents.missing")}
        />
        <Detail
          label={t("school.documents.trainingPlan")}
          value={profile.trainingPlan ?? t("school.documents.missing")}
        />
        <Detail
          label={t("school.documents.learningPaths")}
          value={profile.learningPaths ?? t("school.documents.missing")}
        />
      </dl>
    </Card>
  );
  if (documentsOnly)
    return (
      <>
        <PageHeading title={t("school.documents.title")} subtitle={t("school.documents.description")} />
        {documents}
      </>
    );

  return (
    <>
      <PageHeading subtitle={t("school.subtitle")} title={t("school.title")} />

      <section className="mb-5 flex flex-col justify-between gap-4 rounded-2xl border border-info-foreground/15 bg-info px-4 py-4 text-info-foreground sm:flex-row sm:items-center">
        <div className="flex gap-3">
          <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
          <div>
            <h2 className="font-medium">{t("school.setupBanner.title")}</h2>
            <p className="mt-1 text-sm">{t("school.setupBanner.description")}</p>
          </div>
        </div>
        <Link
          className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground shadow-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
          data-control-id="school.continue"
          to={`${pageDefinitions.schoolSetup.path}?step=${documentsComplete ? 1 : 3}`}
        >
          {t("school.setupBanner.action")}
        </Link>
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card>
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-medium">{t("school.profile.title")}</h2>
              <Status complete />
            </div>
            <EditLink step={1} />
          </div>
          <dl className="space-y-3">
            <Detail label={t("school.profile.schoolName")} value={profile.schoolName} />
            <Detail label={t("school.profile.foundation")} value={profile.foundation} />
            <Detail label={t("school.profile.region")} value={profile.region} />
            <Detail label={t("school.profile.staff")} value={staff} />
          </dl>
        </Card>

        <Card>
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-medium">{t("school.culture.title")}</h2>
              <Status complete />
            </div>
            <EditLink step={2} />
          </div>
          <dl className="space-y-3">
            <Detail label={t("school.culture.educationalType")} value={profile.educationalType} />
            <Detail label={t("school.culture.organisationType")} value={profile.organisationType} />
            <Detail label={t("school.culture.schoolType")} value={profile.schoolType} />
            <Detail label={t("school.culture.specification")} value={profile.specification} />
          </dl>
        </Card>

        {documents}
      </div>
    </>
  );
}

export function SchoolDocumentsPage() {
  return <SchoolPage documentsOnly />;
}
